import { AxiosInstance } from "axios";
import { isCancelled, validateBoardId } from "helpers";
import { createLogger } from "logging";
import { AddBoardModel } from "models/AddBoardModel";
import { BoardModel } from "models/BoardModel";
import { BoardSettingsModel } from "models/BoardSettingsModel";
import { BoardStatisticsModel } from "models/BoardStatisticsModel";
import { BoardWithPermissionsModel } from "models/BoardWithPermissionsModel";
import { PatchBoardModel } from "models/PatchBoardModel";
import { SiveActionResult } from "models/SiveActionResult";

import { DownloadApi } from "./DownloadApi";

const logger = createLogger("BoardApi");

export class BoardApi {
  constructor(
    private axios: AxiosInstance,
    private downloadApi: DownloadApi
  ) {}

  async updateBoard(model: PatchBoardModel): Promise<SiveActionResult> {
    const response = await this.axios.patch<SiveActionResult>(`api/boards/${model.id}`, model);
    return response.data;
  }

  async createBoard(model: AddBoardModel): Promise<BoardModel> {
    const response = await this.axios.post<BoardModel>(`api/boards`, model);
    return response.data;
  }

  async deleteBoard(id: number): Promise<SiveActionResult> {
    const response = await this.axios.delete<SiveActionResult>(`api/boards/${id}`);
    return response.data;
  }

  async getBoard(
    boardId: string | number | undefined | null,
    signal?: AbortSignal
  ): Promise<BoardWithPermissionsModel | undefined> {
    const boardIdToUse = boardId?.toString() ?? "";
    if (!boardIdToUse) {
      throw new Error("No boardId provided");
    }

    const isValidBoardId = validateBoardId(boardIdToUse);
    if (!isValidBoardId.isValid) {
      logger.debug(`getBoard(): Skipped for invalid boardId ${boardIdToUse}`, isValidBoardId.failures);
      return undefined;
    }

    try {
      const response = await this.axios.get<BoardWithPermissionsModel>(`api/boards/${boardIdToUse}`, { signal });
      return response.data;
    } catch (error) {
      if (isCancelled(error)) {
        return undefined;
      }

      logger.error(`getBoard(): Failed for ${boardIdToUse}`, error);
      throw error;
    }
  }

  async getBoardSettings(
    boardId: string | number | undefined | null,
    signal?: AbortSignal
  ): Promise<BoardSettingsModel | undefined> {
    const boardIdToUse = boardId?.toString() ?? "";
    if (!boardIdToUse) {
      throw new Error("No boardId provided");
    }

    const isValidBoardId = validateBoardId(boardIdToUse);
    if (!isValidBoardId.isValid) {
      logger.debug(`getBoardSettings(): Skipped for invalid boardId ${boardIdToUse}`, isValidBoardId.failures);
      return undefined;
    }

    try {
      const response = await this.axios.get<BoardSettingsModel>(`api/boards/${boardIdToUse}/settings`, { signal });
      return response.data;
    } catch (error) {
      if (isCancelled(error)) {
        return undefined;
      }

      logger.error(`getBoardSettings(): Failed for ${boardIdToUse}`, error);
      throw error;
    }
  }

  async getBoardStatistics(
    boardId: string | number | undefined | null,
    signal?: AbortSignal
  ): Promise<BoardStatisticsModel | undefined> {
    const boardIdToUse = boardId?.toString() ?? "";
    if (!boardIdToUse) {
      throw new Error("No boardId provided");
    }

    try {
      const response = await this.axios.get<BoardStatisticsModel>(`api/boards/${boardIdToUse}/statistics`, { signal });
      return response.data;
    } catch (error) {
      if (isCancelled(error)) {
        return undefined;
      }

      logger.error(`getBoardStatistics(): Failed to fetch board statistics for ${boardIdToUse}`, error);
      throw error;
    }
  }

  async getBoardConfigZip(id: number): Promise<void> {
    await this.downloadApi.handleDownload(`api/boards/${id}/export`);
  }

  async getForEditing(boardId: string | number, signal?: AbortSignal) {
    const response = await this.axios.get(`api/boards/${boardId}/edit`, { signal });
    return response.data;
  }
}
